import { Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography } from "@mui/material";
import { GlobalState } from "context/GlobalContext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import RenderInputField from "components/AudienceDashboard/ReusableComponents/RenderInputField/RenderInputField";
import { filterColArry } from "./data";
import { getColDefaultValue, isColumnDefault } from "components/EboStoreV4/data";
import { AddRounded, CloseRounded, DeleteOutlineRounded, FacebookRounded, PauseRounded, PlayArrowRounded, SearchRounded } from "@mui/icons-material";
import { constraintsOptions, inputTypes, selectOptKeyName } from "data/globalData";
import moment from "moment";
import { AudienceDashboardState } from "context/AudienceDashboardContext";
import { AuthState } from "context/AuthContext";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";

const cardSx = {
	bgcolor: "#fff",
	border: "1px solid #e6e8ec",
	borderRadius: 2,
	overflow: "hidden",
};

const PAGE_SIZES = [10, 20, 50, 100];

const audienceColumns = [
	{ field: "audience_name", label: "Audience", minWidth: 240, wrap: true },
	{ field: "ad_account_name", label: "Ad account", minWidth: 200, wrap: true },
	{ field: "table_name", label: "Table", minWidth: 140 },
	{ field: "frequency", label: "Frequency", minWidth: 120, align: "center" },
	{ field: "email", label: "Created by", minWidth: 160 },
	{ field: "row_count", label: "Customers", minWidth: 110, align: "right" },
	{ field: "created_at", label: "Created", minWidth: 160 },
	{ field: "updated_at", label: "Last updated", minWidth: 160 },
	{ field: "status", label: "Status", minWidth: 110, align: "center" },
	{ field: "message", label: "Remarks", minWidth: 180 },
	{ field: "is_active", label: "Action", minWidth: 108, align: "center", sortable: false },
];

const headCellSx = {
	fontWeight: 600,
	fontSize: "0.72rem",
	letterSpacing: "0.04em",
	textTransform: "uppercase",
	color: "#6b7280",
	bgcolor: "#f8f9fb",
	borderBottom: "1px solid #eef0f3",
	py: 1.25,
	px: 1.5,
	whiteSpace: "nowrap",
};

const bodyCellSx = {
	borderBottom: "1px solid #f3f4f6",
	py: 1.15,
	px: 1.5,
	fontSize: "0.875rem",
	color: "#111827",
	verticalAlign: "middle",
	bgcolor: "#fff",
};

const sameAudienceRows = (prev, next) => {
	if (!Array.isArray(prev) || prev.length !== next.length) return false;
	for (let i = 0; i < next.length; i += 1) {
		const a = prev[i];
		const b = next[i];
		if (
			a?.id !== b?.id ||
			a?.updated_at !== b?.updated_at ||
			a?.message !== b?.message ||
			a?.is_active !== b?.is_active ||
			a?.row_count !== b?.row_count
		) {
			return false;
		}
	}
	return true;
};

const HoverText = ({ value, sx, lines = 1 }) => {
	const text = value === null || value === undefined || value === "" ? "—" : String(value);
	const wrap = lines > 1;
	return (
		<Tooltip title={text} placement="top" enterDelay={400}>
			<Box
				component="span"
				sx={{
					display: wrap ? "-webkit-box" : "block",
					flex: 1,
					minWidth: 0,
					maxWidth: "100%",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: wrap ? "normal" : "nowrap",
					wordBreak: wrap ? "break-word" : "normal",
					WebkitLineClamp: wrap ? lines : undefined,
					WebkitBoxOrient: wrap ? "vertical" : undefined,
					lineHeight: 1.35,
					...sx,
				}}>
				{text}
			</Box>
		</Tooltip>
	);
};

const statusMeta = (message) => {
	if (message === "Successfully pushed") return { label: "Success", bg: "#ecfdf3", color: "#027a48" };
	if (message === "Running...") return { label: "Pending", bg: "#fff7ed", color: "#c2410c" };
	return { label: "Error", bg: "#fef2f2", color: "#b91c1c" };
};

const frequencyLabel = (value) => {
	const raw = String(value || "").trim().toLowerCase().replace(/[\s-]/g, "_");
	if (raw === "one_time" || raw === "onetime") return "One time";
	if (raw === "daily") return "Daily";
	return value || "—";
};

const isDailyFrequency = (value) => String(value || "").trim().toLowerCase() === "daily";

const isOneTimeFrequency = (value) => {
	const raw = String(value || "").trim().toLowerCase().replace(/[\s-]/g, "_");
	return raw === "one_time" || raw === "onetime";
};

const deleteCopy = (audience) => {
	if (isDailyFrequency(audience?.frequency)) {
		return {
			kind: "Daily",
			tone: { bg: "#eff6ff", color: "#1d4ed8" },
			detail: "This audience is pushed every day. Deleting it stops the daily schedule and removes it from this list.",
		};
	}
	if (isOneTimeFrequency(audience?.frequency)) {
		return {
			kind: "One time",
			tone: { bg: "#f3f4f6", color: "#374151" },
			detail: "This audience runs once, when it is created. After that it does not run again. Deleting it only removes it from this list.",
		};
	}
	return {
		kind: frequencyLabel(audience?.frequency),
		tone: { bg: "#f3f4f6", color: "#374151" },
		detail: "This removes the audience from the list.",
	};
};

const formatCount = (value) => {
	if (value === null || value === undefined || value === "") return "—";
	const count = Number(value);
	return Number.isFinite(count) ? count.toLocaleString("en-IN") : value;
};

const formatWhen = (value) => (value ? moment(value).format("DD MMM YYYY, HH:mm") : "—");

const Pill = ({ label, value, bg, color, selected, onClick }) => (
	<Stack
		component="button"
		type="button"
		onClick={onClick}
		direction="row"
		spacing={0.75}
		alignItems="center"
		sx={{
			px: 1.25,
			py: 0.6,
			borderRadius: 10,
			bgcolor: bg,
			color,
			border: "1px solid",
			borderColor: selected ? color : "transparent",
			boxShadow: selected ? `inset 0 0 0 1px ${color}` : "none",
			cursor: "pointer",
			font: "inherit",
		}}>
		<Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{label}</Typography>
		<Typography sx={{ fontSize: "0.75rem", fontWeight: 700 }}>{value}</Typography>
	</Stack>
);

const AudienceRows = React.memo(function AudienceRows({ rows, onStatus, onToggleActive, onDelete }) {
	return rows.map((row) => {
		const meta = statusMeta(row.message);
		const filterKey = meta.label === "Success" ? "success" : meta.label === "Error" ? "error" : "";
		const daily = isDailyFrequency(row.frequency);
		const frequency = frequencyLabel(row.frequency);
		const oneTime = row.frequency === "one_time";
		return (
			<TableRow key={row.id} hover sx={{ "&:hover td": { bgcolor: "#fafbfc" } }}>
				<TableCell sx={{ ...bodyCellSx, minWidth: 240 }}>
					<HoverText lines={2} value={row.audience_name} sx={{ fontWeight: 600 }} />
				</TableCell>
				<TableCell sx={{ ...bodyCellSx, minWidth: 200 }}>
					<HoverText lines={2} value={row.ad_account_name} sx={{ color: "text.secondary" }} />
				</TableCell>
				<TableCell sx={{ ...bodyCellSx, maxWidth: 220 }}>
					<HoverText value={row.table_name} />
				</TableCell>
				<TableCell align="center" sx={bodyCellSx}>
					<Chip
						size="small"
						label={frequency}
						sx={{
							height: 24,
							fontWeight: 600,
							fontSize: "0.75rem",
							bgcolor: daily ? "#eff6ff" : "#f3f4f6",
							color: daily ? "#1d4ed8" : "#374151",
						}}
					/>
				</TableCell>
				<TableCell sx={{ ...bodyCellSx, maxWidth: 200 }}>
					<HoverText value={row.email} sx={{ color: "text.secondary" }} />
				</TableCell>
				<TableCell align="right" sx={bodyCellSx}>
					<HoverText value={formatCount(row.row_count)} sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }} />
				</TableCell>
				<TableCell sx={bodyCellSx}>
					<HoverText value={formatWhen(row.created_at)} sx={{ color: "text.secondary" }} />
				</TableCell>
				<TableCell sx={bodyCellSx}>
					<HoverText value={formatWhen(row.updated_at)} sx={{ color: "text.secondary" }} />
				</TableCell>
				<TableCell align="center" sx={bodyCellSx}>
					<Tooltip title={row.message || meta.label}>
						<Chip
							size="small"
							label={meta.label}
							onClick={
								filterKey
									? (event) => {
											event.stopPropagation();
											onStatus(filterKey);
										}
									: undefined
							}
							sx={{
								height: 24,
								fontWeight: 700,
								fontSize: "0.75rem",
								bgcolor: meta.bg,
								color: meta.color,
								cursor: filterKey ? "pointer" : "default",
							}}
						/>
					</Tooltip>
				</TableCell>
				<TableCell sx={{ ...bodyCellSx, maxWidth: 240 }}>
					<HoverText value={row.message || "Pending..."} sx={{ color: "text.secondary" }} />
				</TableCell>
				<TableCell align="center" sx={bodyCellSx}>
					<Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
						{!oneTime && (
							<Tooltip title={row.is_active ? "Stop" : "Resume"}>
								<IconButton
									size="small"
									onClick={(event) => {
										event.stopPropagation();
										event.preventDefault();
										const audience = row;
										const active = row.is_active;
										setTimeout(() => onToggleActive(audience, active), 0);
									}}
									sx={{
										color: row.is_active ? "#b45309" : "#0369a1",
										bgcolor: row.is_active ? "#fff7ed" : "#f0f9ff",
										borderRadius: 1.25,
										"&:hover": { bgcolor: row.is_active ? "#ffedd5" : "#e0f2fe" },
									}}>
									{row.is_active ? <PauseRounded sx={{ fontSize: 18 }} /> : <PlayArrowRounded sx={{ fontSize: 18 }} />}
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									event.preventDefault();
									const audience = row;
									setTimeout(() => onDelete(audience), 0);
								}}
								sx={{
									color: "#b91c1c",
									bgcolor: "#fef2f2",
									borderRadius: 1.25,
									"&:hover": { bgcolor: "#fee2e2" },
								}}>
								<DeleteOutlineRounded sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					</Stack>
				</TableCell>
			</TableRow>
		);
	});
});

const loadBarSx = {
	flexShrink: 0,
	height: 3,
	width: "100%",
	overflow: "hidden",
	bgcolor: "transparent",
	"& .bar": {
		display: "none",
		height: "100%",
		width: "35%",
		bgcolor: "#111827",
		willChange: "transform",
	},
	"&.on": { bgcolor: "#e5e7eb" },
	"&.on .bar": {
		display: "block",
		animation: "audienceLoad 0.65s linear infinite",
	},
	"@keyframes audienceLoad": {
		from: { transform: "translate3d(-100%, 0, 0)" },
		to: { transform: "translate3d(280%, 0, 0)" },
	},
};

const AudienceTable = ({ rows, loading, sortField, sortDir, onSort, onStatus, onToggleActive, onDelete }) => (
	<Box sx={{ minWidth: 0 }}>
		<TableContainer sx={{ width: "100%", maxHeight: "calc(100vh - 280px)", overflow: "auto", bgcolor: "#fff" }}>
		<Table stickyHeader size="small" sx={{ minWidth: 1280 }}>
			<TableHead>
				<TableRow>
					{audienceColumns.map((column) => (
						<TableCell key={column.field} align={column.align || "left"} sx={{ ...headCellSx, minWidth: column.minWidth }}>
							{column.sortable === false ? (
								column.label
							) : (
								<TableSortLabel
									active={sortField === column.field}
									direction={sortField === column.field ? sortDir : "asc"}
									onClick={() => onSort(column.field)}
									sx={{
										color: "#6b7280 !important",
										"& .MuiTableSortLabel-icon": { color: "#6b7280 !important" },
									}}>
									{column.label}
								</TableSortLabel>
							)}
						</TableCell>
					))}
				</TableRow>
			</TableHead>
			<TableBody>
				{loading && !rows.length ? (
					<TableRow>
						<TableCell colSpan={audienceColumns.length} align="center" sx={{ py: 8, border: 0, bgcolor: "#fff" }}>
							<CircularProgress size={22} />
						</TableCell>
					</TableRow>
				) : !rows.length ? (
					<TableRow>
						<TableCell colSpan={audienceColumns.length} align="center" sx={{ py: 8, border: 0, color: "text.secondary", bgcolor: "#fff" }}>
							No audiences
						</TableCell>
					</TableRow>
				) : (
					<AudienceRows rows={rows} onStatus={onStatus} onToggleActive={onToggleActive} onDelete={onDelete} />
				)}
			</TableBody>
		</Table>
	</TableContainer>
	</Box>
);

const FacebookAudiencePush = () => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const { fbDrawerListItems, setFbDrawerListItems } = AudienceDashboardState();

	const [loading, setLoading] = useState(false);
	const [open, setOpen] = React.useState(false);
	const [selected, setSelected] = React.useState({});
	const [statusPrompt, setStatusPrompt] = useState(null);

	const defaultData = filterColArry?.reduce((acc, row) => {
		acc[row.key] = getColDefaultValue(row);
		return acc;
	}, {});
	const [filterData, setFilterData] = useState({ ...defaultData });
	const [logs, setLogs] = useState([]);
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [statusCounts, setStatusCounts] = useState({ all: 0, success: 0, error: 0 });
	const [sortModel, setSortModel] = useState([{ field: "created_at", sort: "desc" }]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(20);
	const [totalRows, setTotalRows] = useState(0);
	const [tableLoading, setTableLoading] = useState(true);
	const [listVersion, setListVersion] = useState(0);

	const [filterColumns, setFilterColumns] = useState(filterColArry);

	useEffect(() => {
		const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
		return () => clearTimeout(timer);
	}, [searchInput]);

	const toggleStatusFilter = useCallback((next) => {
		setStatusFilter((current) => (current === next ? "" : next));
	}, []);

	const getDropdownOptionsAPI = async () => {
		setLoadingOpen(true);
		try {
			const res = await pythonVmInstance({
				url: `/audience/facebook/filters`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "get",
			});
			setLoadingOpen(false);
			return res?.data?.result || {};
		} catch (error) {
			setSnackBarOpen(true);
			console.log("asdfghjkl", error);
			setSnackBar({
				...snackBar,
				message: `failed to load DropdownItems`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
			return {};
		}
	};
	const fetchDropdownOptions = async () => {
		if (fbDrawerListItems) return fbDrawerListItems;
		else {
			const result = await getDropdownOptionsAPI();
			setFbDrawerListItems(result);
			return result || {};
		}
	};
	const fetch = async () => {
		const result = (await fetchDropdownOptions()) || {};
		setFilterColumns((allColumns) => {
			let obj = result;
			return allColumns.map((row) => {
				if (obj?.[row.key] && [inputTypes.SELECT.key, inputTypes.MULTIPLE_SELECT.key].includes(row?.input_type)) {
					let opt = row[selectOptKeyName] ? row[selectOptKeyName] : {};
					opt = { ...opt, list: obj[row.key] };
					return { ...row, [selectOptKeyName]: opt };
				} else return row;
			});
		});
	};
	useEffect(() => {
		fetch();
	}, []);

	const fetchSchedules = async (limit, offset) => {
		try {
			const params = new URLSearchParams({
				offset: String(offset),
				limit: String(limit),
			});
			if (search) params.set("search", search);
			if (statusFilter) params.set("status", statusFilter);
			const sort = sortModel?.[0];
			if (sort?.field && sort?.sort) {
				params.set("sort_by", sort.field);
				params.set("sort_dir", sort.sort);
			}
			const res = await pythonVmInstance({
				url: `audience/facebook/audiences?${params.toString()}`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "get",
			});
			const payload = res.data.result || {};
			setStatusCounts((prev) => {
				const next = {
					all: Number(payload.all_count) || 0,
					success: Number(payload.success_count) || 0,
					error: Number(payload.error_count) || 0,
				};
				if (prev.all === next.all && prev.success === next.success && prev.error === next.error) return prev;
				return next;
			});
			return payload;
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Unable to fetch Audiences`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	const fetchSchedulesRef = useRef(fetchSchedules);
	fetchSchedulesRef.current = fetchSchedules;
	const logsRef = useRef(logs);
	logsRef.current = logs;
	const loadingBarRef = useRef(null);
	const queryKey = `${search}::${statusFilter}::${sortModel.map((item) => `${item.field}:${item.sort}`).join(",")}`;
	const queryKeyRef = useRef(queryKey);
	const showLoadingBar = () => loadingBarRef.current?.classList.add("on");
	const hideLoadingBar = () => loadingBarRef.current?.classList.remove("on");

	useEffect(() => {
		if (queryKeyRef.current !== queryKey) {
			queryKeyRef.current = queryKey;
			if (page !== 0) {
				setPage(0);
				return;
			}
		}
		let ignore = false;
		const loadAudiences = async (silent) => {
			const limit = pageSize;
			const offset = page * pageSize;
			if (!silent) {
				showLoadingBar();
				if (!logsRef.current.length) setTableLoading(true);
			}
			const payload = await fetchSchedulesRef.current(limit, offset);
			if (ignore) return;
			if (!silent) {
				hideLoadingBar();
				setTableLoading(false);
			}
			if (!payload || !Array.isArray(payload.result)) return;
			const total = Number(payload.total_rows) || 0;
			if (page > 0 && payload.result.length === 0 && total > 0) {
				setPage(page - 1);
				return;
			}
			setLogs((prev) => (silent && sameAudienceRows(prev, payload.result) ? prev : payload.result));
			setTotalRows((count) => (count === total ? count : total));
		};
		loadAudiences(false);
		const interval = setInterval(() => loadAudiences(true), 60000);
		return () => {
			ignore = true;
			hideLoadingBar();
			clearInterval(interval);
		};
	}, [page, pageSize, queryKey, listVersion]);

	const handleRunQueryClick = async (e) => {
		e.preventDefault();
		setLoading(true);
		let pendingFields = [];
		filterColArry?.forEach((attr) => {
			if (isColumnDefault(attr, filterData?.[attr?.key]) && attr?.constraints?.[constraintsOptions.NOTNULL.key] === true)
				pendingFields.push(attr);
		});

		if (pendingFields?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Enter values for ${pendingFields.map((row) => row?.name)}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoading(false);
			return;
		}

		try {
			await pythonVmInstance({
				url: `/audience/facebook/create_audience`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "post",
				data: {
					...filterData,
					email: user.email,
				},
			}).then(async (res) => {
				setPage(0);
				setListVersion((version) => version + 1);

				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: res?.data.message ?? "Created Successfullyy",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoading(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoading(false);
		}
	};
	const handleActiveInactivButton = async (row, value) => {
		setLoadingOpen(true);

		try {
			const body = { is_active: !value };
			await pythonVmInstance({
				url: `/audience/facebook/update?id=${row.id}`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "patch",
				data: body,
			}).then(async (res) => {
				setLogs((prev) =>
					prev.map((item) => {
						if (item.id === row.id) return { ...item, ...body };
						else return item;
					})
				);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: res?.data.message ?? "Created Successfullyy",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};
	const handleDeleteAudience = async () => {
		setLoadingOpen(true);
		let row = selected;
		try {
			await pythonVmInstance({
				url: `/audience/facebook/delete?id=${row.id}`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "delete",
			}).then(async (res) => {
				setLogs((prev) => prev.filter((item) => item.id !== row.id));
				setListVersion((version) => version + 1);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: res?.data.message ?? "Deleted Successfullyy",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
		handleClose();
	};

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setSelected({});
		setOpen(false);
	};

	const sortField = sortModel[0]?.field || "created_at";
	const sortDir = sortModel[0]?.sort || "desc";

	const handleSort = useCallback((field) => {
		setSortModel((current) => {
			const active = current[0];
			if (active?.field === field) return [{ field, sort: active.sort === "asc" ? "desc" : "asc" }];
			return [{ field, sort: "asc" }];
		});
	}, []);

	const openDelete = useCallback((audience) => {
		setSelected(audience);
		setOpen(true);
	}, []);

	const handleToggleActive = useCallback((audience, active) => {
		setStatusPrompt({ row: audience, active });
	}, []);

	return (
		<Stack spacing={2.5} sx={{ bgcolor: "#f7f8fa", borderRadius: 2, p: { xs: 1.5, md: 2 }, mt: 1.5 }}>
			<Box component="form" onSubmit={handleRunQueryClick} sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
				<Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: 1.5,
							display: "grid",
							placeItems: "center",
							bgcolor: "#f3f4f6",
							color: "#111827",
							flexShrink: 0,
						}}>
						<FacebookRounded sx={{ fontSize: 22 }} />
					</Box>
					<Box minWidth={0}>
						<Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", lineHeight: 1.2 }}>
							Create audience
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
							Push a customer table to a Facebook ad account, once or every day.
						</Typography>
					</Box>
				</Stack>

				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 2,
						alignItems: "flex-end",
						"& > .field > .MuiStack-root": {
							minHeight: "0 !important",
						},
					}}>
					{filterColumns?.map((col) => (
						<Box key={col.key} className="field" sx={{ flex: "1 1 200px", minWidth: 0 }}>
							<RenderInputField
								column={col}
								data={filterData}
								setData={setFilterData}
								selectOptObjName={selectOptKeyName}
								width="100%"
								longWidth="100%"
								disabled={false}
							/>
						</Box>
					))}
					<Button
						variant="contained"
						type="submit"
						disabled={loading}
						startIcon={loading ? <CircularProgress color="inherit" size={16} /> : <AddRounded />}
						sx={{
							height: 40,
							px: 2.5,
							minWidth: 132,
							flex: "0 0 auto",
							textTransform: "none",
							fontWeight: 600,
							borderRadius: 1.5,
							boxShadow: "none",
							"&:hover": { boxShadow: "none" },
						}}>
						{loading ? "Creating..." : "Create"}
					</Button>
				</Box>
			</Box>

			<Box sx={{ ...cardSx, display: "flex", flexDirection: "column", minWidth: 0 }}>
				<Stack
					direction={{ xs: "column", md: "row" }}
					spacing={1.5}
					alignItems={{ xs: "flex-start", md: "center" }}
					justifyContent="space-between"
					sx={{ px: 2.5, py: 2, borderBottom: "1px solid #eef0f3" }}>
					<Box>
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827" }}>Audiences</Typography>
							<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#16a34a" }} />
						</Stack>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
							{statusFilter ? `Showing ${statusFilter} rows. Click the same status again to show all.` : "Click Success or Error to show only those rows."}
							{search ? ` Matches for “${search}”.` : ""}
						</Typography>
					</Box>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
						<TextField
							size="small"
							value={searchInput}
							onChange={(event) => setSearchInput(event.target.value)}
							placeholder="Search name, account, table, email"
							sx={{
								width: { xs: "100%", sm: 280 },
								"& .MuiOutlinedInput-root": {
									height: 40,
									borderRadius: 1.5,
									bgcolor: "#f8f9fb",
									"& fieldset": { borderColor: "#e6e8ec" },
									"&:hover fieldset": { borderColor: "#d1d5db" },
									"&.Mui-focused fieldset": { borderColor: "#111827" },
								},
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchRounded sx={{ fontSize: 18, color: "#6b7280" }} />
									</InputAdornment>
								),
								endAdornment: searchInput ? (
									<InputAdornment position="end">
										<IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput("")}>
											<CloseRounded sx={{ fontSize: 16 }} />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
						/>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
						<Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151", whiteSpace: "nowrap", px: 0.5 }}>
							Total {statusCounts.all}
						</Typography>
						<Pill label="Success" value={statusCounts.success} bg="#ecfdf3" color="#027a48" selected={statusFilter === "success"} onClick={() => toggleStatusFilter("success")} />
						<Pill label="Error" value={statusCounts.error} bg="#fef2f2" color="#b91c1c" selected={statusFilter === "error"} onClick={() => toggleStatusFilter("error")} />
					</Stack>
					</Stack>
				</Stack>
				<Box sx={{ minWidth: 0 }}>
					<Box ref={loadingBarRef} sx={loadBarSx}>
						<Box className="bar" />
					</Box>
					<AudienceTable
						rows={logs}
						loading={tableLoading}
						sortField={sortField}
						sortDir={sortDir}
						onSort={handleSort}
						onStatus={toggleStatusFilter}
						onToggleActive={handleToggleActive}
						onDelete={openDelete}
					/>
					<TablePagination
						component="div"
						count={totalRows}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={PAGE_SIZES}
						onPageChange={(_, nextPage) => setPage(nextPage)}
						onRowsPerPageChange={(event) => {
							setPageSize(parseInt(event.target.value, 10));
							setPage(0);
						}}
						labelRowsPerPage="Rows"
						labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
						sx={{ borderTop: "1px solid #eef0f3", bgcolor: "#fff", flexShrink: 0 }}
					/>
				</Box>
			</Box>

			<Dialog
				open={open}
				onClose={handleClose}
				onClick={(event) => event.stopPropagation()}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 440, overflow: "hidden" } }}>
				{(() => {
					const copy = deleteCopy(selected);
					return (
						<>
							<DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
								<Stack direction="row" spacing={1.5} alignItems="flex-start">
									<Box
										sx={{
											width: 40,
											height: 40,
											borderRadius: 1.5,
											display: "grid",
											placeItems: "center",
											bgcolor: "#fef2f2",
											color: "#b91c1c",
											flexShrink: 0,
										}}>
										<DeleteOutlineRounded sx={{ fontSize: 22 }} />
									</Box>
									<Box minWidth={0}>
										<Typography id="alert-dialog-title" sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", lineHeight: 1.3 }}>
											Delete audience
										</Typography>
										<Typography id="alert-dialog-description" variant="body2" sx={{ mt: 0.75, color: "#4b5563" }}>
											{copy.detail}
										</Typography>
									</Box>
								</Stack>
								<Box
									sx={{
										mt: 2.5,
										px: 1.5,
										py: 1.25,
										borderRadius: 1.5,
										bgcolor: "#f8f9fb",
										border: "1px solid #eef0f3",
									}}>
									<Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#111827", wordBreak: "break-word" }}>
										{selected?.audience_name || "This audience"}
									</Typography>
									<Chip
										size="small"
										label={copy.kind}
										sx={{ mt: 1, height: 24, fontWeight: 700, fontSize: "0.75rem", bgcolor: copy.tone.bg, color: copy.tone.color }}
									/>
								</Box>
							</DialogContent>
							<DialogActions sx={{ px: 3, pt: 1.5, pb: 2.5, gap: 1 }}>
								<Button
									type="button"
									onClick={handleClose}
									variant="outlined"
									color="inherit"
									sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e6e8ec", color: "#374151", borderRadius: 1.5 }}>
									Cancel
								</Button>
								<Button
									type="button"
									onClick={handleDeleteAudience}
									variant="contained"
									color="error"
									sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: 1.5, "&:hover": { boxShadow: "none" } }}>
									Delete
								</Button>
							</DialogActions>
						</>
					);
				})()}
			</Dialog>

			<Dialog
				open={Boolean(statusPrompt)}
				onClose={() => setStatusPrompt(null)}
				onClick={(event) => event.stopPropagation()}
				PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 440, overflow: "hidden" } }}>
				{statusPrompt && (
					<>
						<DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
							<Stack direction="row" spacing={1.5} alignItems="flex-start">
								<Box
									sx={{
										width: 40,
										height: 40,
										borderRadius: 1.5,
										display: "grid",
										placeItems: "center",
										flexShrink: 0,
										bgcolor: statusPrompt.active ? "#fff7ed" : "#f0f9ff",
										color: statusPrompt.active ? "#b45309" : "#0369a1",
									}}>
									{statusPrompt.active ? <PauseRounded sx={{ fontSize: 22 }} /> : <PlayArrowRounded sx={{ fontSize: 22 }} />}
								</Box>
								<Box minWidth={0}>
									<Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", lineHeight: 1.3 }}>
										{statusPrompt.active ? "Stop audience" : "Resume audience"}
									</Typography>
									<Typography variant="body2" sx={{ mt: 0.75, color: "#4b5563" }}>
										{statusPrompt.active
											? "This audience is pushed every day. Stopping it pauses the daily schedule until you resume it."
											: "This audience will be pushed every day again from the next run."}
									</Typography>
								</Box>
							</Stack>
							<Box
								sx={{
									mt: 2.5,
									px: 1.5,
									py: 1.25,
									borderRadius: 1.5,
									bgcolor: "#f8f9fb",
									border: "1px solid #eef0f3",
								}}>
								<Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#111827", wordBreak: "break-word" }}>
									{statusPrompt.row?.audience_name || "This audience"}
								</Typography>
								<Chip
									size="small"
									label="Daily"
									sx={{ mt: 1, height: 24, fontWeight: 700, fontSize: "0.75rem", bgcolor: "#eff6ff", color: "#1d4ed8" }}
								/>
							</Box>
						</DialogContent>
						<DialogActions sx={{ px: 3, pt: 1.5, pb: 2.5, gap: 1 }}>
							<Button
								type="button"
								onClick={() => setStatusPrompt(null)}
								variant="outlined"
								color="inherit"
								sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e6e8ec", color: "#374151", borderRadius: 1.5 }}>
								Cancel
							</Button>
							<Button
								type="button"
								variant="contained"
								color={statusPrompt.active ? "warning" : "info"}
								onClick={() => {
									const prompt = statusPrompt;
									setStatusPrompt(null);
									handleActiveInactivButton(prompt.row, prompt.active);
								}}
								sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: 1.5, "&:hover": { boxShadow: "none" } }}>
								{statusPrompt.active ? "Stop" : "Resume"}
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Stack>
	);
};

export default FacebookAudiencePush;
